import cn from 'classnames';
import React, { useState, useEffect } from 'react';
import { difference } from 'ramda';

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import styles from 'styles/recommendations-list.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import filterStyles from 'styles/filter-input.module.scss';

/* API */
import { rateItem } from 'lib/API';

/* Assets */
import arrowDown from 'assets/images/Arrow_Dropdown.svg';

/* Redux */
import { useSelector, useDispatch } from 'react-redux';
import { setPage } from 'app/slices/jobsSlice';
import { thumbsUpClick, thumbsDownClick, revertThumbsClick } from 'app/slices/ratingsSlice';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from "timeme.js";

/* Material UI and other UI Dependencies */
import Container from '@material-ui/core/Container';
import Typography from "@material-ui/core/Typography";
import withWidth from '@material-ui/core/withWidth';
import Box from '@material-ui/core/Box';
import RecommendationsCard from 'components/RecommendationsCard';
import Button from "@material-ui/core/Button";
import ScrollToTopButton from 'components/ScrollToTopButton';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useSnackbar } from 'notistack';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';

/* UI Components */
import Tag from "components/Tag";

const Jobs =({locale, width}) => {

  const {
    results,
    activeSoc,
    currentSoc,
    availableFilters,
    pageSize,
    page
  } = useSelector((state) => state.jobs);

  const [sortBy, setSortBy] = useState('NEWEST');
  const [sortingOpen, setSortingOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(['careerPathName']);
  const [displaySocCode, setDisplaySocCode] = useState(activeSoc !== '' ? activeSoc : currentSoc);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  
  const ratingsLookup = useSelector((state) => state.ratings.results.lookup);
  const careers = useSelector((state) => state.careers.results);
  const currentCareerTitle = useSelector((state) => state.experience.form.soc_title);

  const displaySocCodeName = activeSoc !== ''
    ? careers.reduce((prev, curr) => curr.soc_code === activeSoc ? curr.title : prev, '')
    : currentCareerTitle;

  /**
   * This new version of filters contains only the current Career Path name
   * until the backend can provide 'Nearby' and 'Part Time' props. In case we
   * need to get back, please check previous versions of this file.
   */
  const translatedAvailableFilters = [{
    title: displaySocCodeName,
    value: 'careerPathName'
  }]
  const translatedFiltersDict = availableFilters.reduce((prev, curr) => {
    if (curr === 'careerPathName') {
      return {...prev, [curr]: displaySocCodeName};
    }
    return {...prev, [curr]: (intl.formatMessage({id: `jobs.filters.${curr}`}))};
  }, {});
  
  const sortingOptions = [
    {value: 'NEWEST', title: intl.formatMessage({id:'ratings.sort.newest', description:'string'})},
    {value: 'OLDEST', title: intl.formatMessage({id:'ratings.sort.oldest', description:'string'})},
  ];

  const sortFunction = {
    NEWEST: (a, b) => parseInt(b.date_posted) - parseInt(a.date_posted),
    OLDEST: (a, b) => parseInt(a.date_posted) - parseInt(b.date_posted),
  }

  const allJobs = Object.keys(results).reduce(
    (prev, currKey) => prev.concat(
      results[currKey].map(job => ({...job, soc_code: currKey}))),
      []
    ).sort(sortFunction[sortBy]);

  /**
   * Standard response items
   */
  var results_soc = displaySocCode ? [...results[displaySocCode]] : undefined;
  if (typeof(results_soc) !== 'undefined') {
    results_soc.sort(sortFunction[sortBy]);
  }
  const responses = activeFilters.indexOf('careerPathName') > -1 && displaySocCode !== '' && typeof(results_soc) !== 'undefined'
    ? results_soc.slice(0, 10)
    : (activeFilters.indexOf('careerPathName') > -1 ? [] : allJobs.slice(0, 10));

  /**
   * This additional response items will be used with the 'ShowMore' button
   * if there are no filters applied
   */
  const more = activeFilters.indexOf('careerPathName') > -1 && displaySocCode !== '' && typeof(results_soc) !== 'undefined'
    ? results_soc.slice(10)
    : (activeFilters.indexOf('careerPathName') > -1 ? [] : allJobs.slice(10));

  /**
   * The number of pages of aditional items available vary
   * depending on the filters applied
   */
  const nPages = Math.ceil(more.length / pageSize);

  const showMore = () => {
    trackEvent('JOBS_MORE');
    dispatch(setPage(page + 1));
  }

  const handleApplyClick = (item) => { 
    trackEvent('JOBS_APPLY', item);
    if (activeSoc !== '') {
      trackEvent('JOBS_APPLY_FILTEREDBYCAREER', activeSoc);
    }
  }

  const handleLikeClick = (item) => () => {
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsUpClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'liked', item, previousRating === 'liked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })
    trackEvent('JOBS_THUMBSUP', item);
    if (activeSoc !== '') {
      trackEvent('JOBS_THUMBSUP_FILTEREDBYCAREER', activeSoc);
    }
  }

  const handleDislikeClick = (item) => () => {
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsDownClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'disliked', item, previousRating === 'disliked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })
    trackEvent('JOBS_THUMBSDOWN', item);
    if (activeSoc !== '') {
      trackEvent('JOBS_THUMBSDOWN_FILTEREDBYCAREER', activeSoc);
    }
  }

  const handleFiltersChange = ({ target: { value: newValue } }) => {
    const filterDict = {
      nearby: 'JOBS_FILTER_NEARBY',
      careerPathName: 'JOBS_FILTER_BYCAREER',
      partTime: 'JOBS_FILTER_PARTTIME',
    }
    const filtersDifference = difference(newValue, activeFilters);
    if (filtersDifference.length > 0) {
      trackEvent(filterDict[filtersDifference[0]]);
    }
    setActiveFilters(newValue);
    if (newValue.indexOf('careerPathName') > -1) {
      setDisplaySocCode(activeSoc !== '' ? activeSoc : currentSoc);
    } else {
      setDisplaySocCode('');
    }
  }

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const smallScreen = ['xs'].indexOf(width) > -1;

  const removeFilter = (tagId) => (e) => {
    e.preventDefault();
    const newActiveFilters = activeFilters.filter(f => f !== tagId);
    setActiveFilters(newActiveFilters);
  }

  const clearAllFilters = () => {
    setDisplaySocCode('');
    setActiveFilters([]);
  }

  useEffect(() => {
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName("JOBS");
    TimeMe.startTimer();
    return () => {
      console.log('Leaving Jobs')
    };
  }, []);

  const handleSortingChange = ({target: { value }}) => {
    setSortBy(value);
    trackEvent(`JOBS_SORT_${value}`);
  }

  const noResults = (activeFilters.indexOf('careerPathName') === -1 && responses.length === 0);

  return(
    <>
      <div className={styles.content}>
        <Container maxWidth="lg">
          <div className={styles.titleFilters}>
            <Typography variant="h1" component="h2">
              {
                noResults ? intl.formatMessage({id: 'jobs.noJobs'}) : intl.formatMessage({id:'jobs.title'})
              }
            </Typography>
            <div className={cn(styles.autocompleteContainer, styles.autocompleteMultipleContainer)}>
              <FormControl
                className={cn({
                  [filterStyles.formControl]: true,
                  [filterStyles.ratingsSortingFocused]: sortingOpen
                })}
              >
                <span className={filterStyles.selectLabel}>
                  {intl.formatMessage({id: sortBy === 'NEWEST' ? 'ratings.sort.newest' : 'ratings.sort.oldest'})}
                </span>
                <Select
                  disableUnderline
                  disabled={responses.length === 0}
                  labelId="sort-select-label"
                  id="sort-select"
                  value={sortBy}
                  onChange={handleSortingChange}
                  onOpen={() => setSortingOpen(true)}
                  onClose={() => setSortingOpen(false)}
                  input={<Input />}
                  renderValue={() => ''}
                  IconComponent={(_props) => <img alt="Arrow down" className={filterStyles.arrowDown} src={arrowDown} />}
                  MenuProps={{
                    PaperProps: {
                      className: filterStyles.selectMenu,
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }}
                  classes={{
                    root: filterStyles.selectRoot,
                    select: filterStyles.selectSelect,
                  }}
                >
                  {sortingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <span className={filterStyles.checkboxLabel}>
                        {option.title}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                className={cn({[filterStyles.formControl]: true, [filterStyles.focused]: filtersOpen})}
              >
                <span className={filterStyles.selectLabel}>
                  {intl.formatMessage({id:'jobs.filter', description:'string'})}
                </span>
                <Select
                  multiple
                  disableUnderline
                  disabled={responses.length === 0}
                  labelId="filters-select-label"
                  id="filters-select"
                  value={activeFilters}
                  onChange={handleFiltersChange}
                  input={<Input />}
                  renderValue={() => ''}
                  IconComponent={(_props) => <img alt="Arrow down" className={filterStyles.arrowDown} src={arrowDown} />}
                  onOpen={() => setFiltersOpen(true)}
                  onClose={() => setFiltersOpen(false)}
                  MenuProps={{
                    PaperProps: {
                      className: filterStyles.selectMenu,
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }}
                  classes={{
                    root: filterStyles.selectRoot,
                    select: filterStyles.selectSelect,
                  }}
                >
                  {translatedAvailableFilters.map((filter) => (
                    <MenuItem key={filter.value} value={filter.value}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        classes={{
                          root: filterStyles.checkboxRoot
                        }}
                        style={{ marginRight: -3, marginLeft: 0, padding: '5px 9px 7px 4px' }}
                        checked={activeFilters.indexOf(filter.value) > -1}
                      />
                      <span className={filterStyles.checkboxLabel}>
                        {filter.title}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          {activeFilters.length > 0 && (
            <>
              <hr className={layoutStyles.separator} />
              <Box m={2} />
              <Typography variant="h3" gutterBottom align={smallScreen ? 'center' : 'inherit'}>
                {intl.formatMessage(
                  {id:'jobs.filterResultsTitle', description:'string'}
                )}
              </Typography>
              <div className={filterStyles.tags}>
                <Typography variant="h5">
                  {intl.formatMessage(
                    {id:'recommendations.filterBy', description:'string'}
                  )}
                </Typography>
                {activeFilters.map(f => (
                  <Tag key={f} text={translatedFiltersDict[f]} onClick={removeFilter(f)} type="filter" />
                ))}
                <span
                  className={cn(layoutStyles.link, filterStyles.clearAll)}
                  onClick={clearAllFilters}
                >
                  {intl.formatMessage({id:'recommendations.clearAllFilters', description:'string'})}
                </span>
              </div>
              <Box m={1} />
            </>
          )}
          {activeFilters.indexOf('careerPathName') === -1 && responses.length ? responses.map(item => (
            <React.Fragment key={item.id}>
              <Box m={3} />
              <RecommendationsCard
                type="job"
                item={item}
                link={item.apply}
                rating={ratingsLookup[item.id]}
                onButtonClick={handleApplyClick(item)}
                onLikeClick={handleLikeClick(item)}
                onDislikeClick={handleDislikeClick(item)}
              />
            </React.Fragment>
          )) : null}
          {responses && responses.length > 0 ? (
            <>
              <hr className={layoutStyles.separator} />
              <Box m={3} />
              {responses.map((item, itemIndex) => (
                <RecommendationsCard
                  key={item.id}
                  type="job"
                  item={item}
                  link={item.apply}
                  rating={ratingsLookup[item.id]}
                  onButtonClick={handleApplyClick(item)}
                  onLikeClick={handleLikeClick(item)}
                  onDislikeClick={handleDislikeClick(item)}
                />
              ))}
            </>
          ) : null}
          {more.length > 0 && page > 0 && (
            <>
              <hr className={layoutStyles.separator} />
              <Box m={4} />
              {more.slice(0, pageSize * page).map(item => (
                <RecommendationsCard
                  key={item.id}
                  type="job"
                  item={item}
                  link={item.apply}
                  rating={ratingsLookup[item.id]}
                  onButtonClick={handleApplyClick(item)}
                  onLikeClick={handleLikeClick(item)}
                  onDislikeClick={handleDislikeClick(item)}
                />
              ))}
            </>
          )}
          <div className={styles.bottomBar}>
            <span className={styles.backToTopPlaceholder} />
            {more.length > 0 && page < nPages ? (
                <Button
                color="primary"
                size="large"
                type="submit"
                variant="contained"
                onClick={showMore}
              >
                {intl.formatMessage(
                  {id:'jobs.showMore', description:'string'}
                )}
              </Button>
            ) : (
              <span />
            )}              
            <ScrollToTopButton toTopEvent="JOBS_TOP" />
          </div>
        </Container>
      </div>
    </>
  )
}

export default withWidth()(Jobs);